<template>
  <div class="list-work-orders">
    <AppPageHeader title="Work Orders" :action="{ title: 'Add new work order', to: { name: 'CreateWorkOrder' } }" />

    <div class="card">
      <BaseTable
        :fields="tableFields"
        :items="results ?? []"
        :is-loading="isPending"
        :is-searchable="true"
        :clickable="true"
        :paginate="true"
        :error="error"
        :next-page="nextPage"
        :prev-page="prevPage"
        :page="currentPage"
        :total-records="totalRecords"
        :total-pages="totalPages"
        :number-per-page="numPerPage"
        @on-search="(val) => search(val)"
        @row-selected="onRowSelected"
      >
        <template #workOrderId-data="{ row }">
          <RouterLink :to="{ name: 'ShowWorkOrder', params: { id: row.id } }">
            <span>{{ row.workOrderId }}</span>
          </RouterLink>
        </template>

        <template #status-data="{ row }">
          <BaseBadge :variant="row.status === WORK_ORDER_STATUS.CLOSED ? 'success' : 'warning'">
            <span class="capitalize">{{ row.status }}</span>
          </BaseBadge>
        </template>

        <template #date-data="{ row }">
          <span>{{ formatDate(row.date) }}</span>
        </template>

        <template #projectId-data="{ row }">
          <router-link v-if="projects && projects?.find(project => project.id === row.projectId)?.id" :to="{ name: 'ShowProject', params: { projectId: row.projectId, customerId: projects?.find(project => project.id === row.projectId)?.customerId } }">
            {{ projects?.find(project => project.id === row.projectId)?.name || row.projectId }}
          </router-link>
          <span v-else>
            {{ row.projectId }}
          </span>
        </template>

        <template #subId-data="{ row }">
          <RouterLink v-if="subcontractors && subcontractors.find(sub => sub.id === row.subId)?.id" :to="{ name: 'ShowSubcontractor', params: { id: row.subId } }">
            <span>{{ subcontractors.find(sub => sub.id === row.subId)?.name}}</span>
          </RouterLink>
          <span v-else>
            {{ row.subId }}
          </span>
        </template>

        <template #total-data="{ row }">
          {{ currency(row.total) }}
        </template>
      </BaseTable>

      <DeleteConfirmationModal
        id="deleteConfirmationModal"
        title="Delete Work Order"
        @delete="deleteWorkOrder"
      >
        <template #modal-body>
          <p>Are you sure you want to delete this work order?</p>
          <p>This will also delete:</p>
          <ul>
            <li>All bills</li>
          </ul>
        </template>
      </DeleteConfirmationModal>
    </div>
  </div>
</template>

<script lang="ts" setup>
import DeleteConfirmationModal from '@/components/app/DeleteConfirmationModal.vue'
import { computed, ref } from 'vue'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import { useHead } from '@unhead/vue'
import WorkOrderService from '@/services/WorkOrderService'
import { useSearchablePagination } from '@/composables/useSearchablePagination'
import { useMutation, useQuery } from '@tanstack/vue-query'
import BaseBadge from '@/components/base/BaseBadge.vue'
import { WORK_ORDER_STATUS, WorkOrder } from '@/models/workOrder'
import { formatDate, currency } from '@/utils'
import useGetAllSubs from '@/composables/useGetAllSubs'
import ProjectService from '@/services/ProjectService'
import { inArray } from '@/utils/queryFilters'
import { useNotification } from '@kyvg/vue3-notification'
import { useRouter } from 'vue-router'

useHead({
  title: 'Work Orders'
})

const { notify } = useNotification()
const $router = useRouter()
const tableFields = [
  { key: 'workOrderId', label: 'WO#', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'date', label: 'Date', sortable: true },
  { key: 'projectId', label: 'Project', sortable: true },
  { key: 'subId', label: 'Contractor', sortable: true },
  { key: 'total', label: 'Total', sortable: true },
]

const numPerPage = ref(25)

const {
  results,
  isPending,
  error,
  search,
  currentPage,
  totalPages,
  totalRecords,
  nextPage,
  prevPage,
} = useSearchablePagination({
  indexName: 'work_orders',
  service: new WorkOrderService(''),
  numPerPage: numPerPage.value,
  defaultOrderBy: 'createdAt',
  defaultOrderDir: 'desc'
})

const onRowSelected = (row: any) => {
  $router.push({ name: 'ShowWorkOrder', params: { id: row.id } })
}

const uniqueProjects = computed(() => {
  return results.value?.map((workOrder: WorkOrder) => workOrder.projectId)
})

const projectService = new ProjectService()
const { data: projects } = useQuery({
  queryKey: ['projects', uniqueProjects, currentPage],
  queryFn: () => projectService.where([inArray('id', uniqueProjects.value)], [])
})

const { data: subcontractors } = useGetAllSubs()

const { mutate: deleteWorkOrder } = useMutation({
  onMutate: async (workOrder: WorkOrder) => {
    const workOrderService = new WorkOrderService('')
    await workOrderService.delete(workOrder.id)
    return workOrder
  },
  onSuccess: () => {
    notify({
      type: 'success',
      title: 'Work order deleted successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Failed to delete work order',
      text: error.message
    })
  },
})

</script>
